import { Injectable } from '@angular/core';
import { SystemEvent } from '../../interfaces/system_events/system-event';


@Injectable({ providedIn: 'root' })
export class SystemEventDismissalService {
  private static readonly STORAGE_KEY = 'reghub.dismissedSystemEvents';
  private static readonly ONE_DAY_MS = 24 * 60 * 60 * 1000;

  /** Snooze a single event's banner for the given duration (defaults to one day). */
  public dismiss(eventID: string, durationMs: number = SystemEventDismissalService.ONE_DAY_MS): void {
    const store = this.read();
    store[eventID] = Date.now() + durationMs;
    this.write(store);
  }

  /** True while an event is within its dismissal window. Expired entries read as not dismissed. */
  public isDismissed(eventID: string): boolean {
    const expiry = this.read()[eventID];
    return expiry !== undefined && Date.now() < expiry;
  }

  /** Drop any currently-dismissed events from a list before it reaches the banner. */
  public filterDismissed(events: SystemEvent[]): SystemEvent[] {
    return events.filter(event => !this.isDismissed(event.id));
  }

  private read(): Record<string, number> {
    try {
      const raw = localStorage.getItem(SystemEventDismissalService.STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      // Corrupt value or storage unavailable (private mode) — treat as no dismissals.
      return {};
    }
  }

  private write(store: Record<string, number>): void {
    const now = Date.now();
    const pruned: Record<string, number> = {};
    for (const [id, expiry] of Object.entries(store)) {
      if (expiry > now) {
        pruned[id] = expiry;
      }
    }

    try {
      localStorage.setItem(SystemEventDismissalService.STORAGE_KEY, JSON.stringify(pruned));
    } catch {
      // Storage full or unavailable — a failed snooze just means the banner reappears.
    }
  }
}
